// Copyright 2026. WebPros International GmbH. All rights reserved.

import React, { FC } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceActions from 'admin/computeResource/actions';
import {
    Alert,
    Button,
    FormField,
    Icon,
    List,
    Status,
    Translate,
} from '@plesk/ui-library';
import { Loader } from 'common/components';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { Dialog } from 'common/components/Dialog/Dialog';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import VGPUPlacements from 'admin/computeResource/page/components/VGPUPlacements/VGPUPlacements';
import {
    IGpuResponse,
    IVGpuProfile,
    GpuStatus,
} from 'common/api/resources/Gpu';
import SelectInput from 'common/components/Select/SelectInput';
import { ISelectOption } from 'common/components/SelectInput';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';

type VGPUTabProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

interface IProfileSelectOption extends ISelectOption {
    value: number;
    label: string;
    profile: IVGpuProfile;
}

const COLUMNS = [
    {
        key: 'colInd',
        title: <Translate content="computeResource.vgpu.list.index" />,
    },
    {
        key: 'colName',
        title: <Translate content="computeResource.vgpu.list.name" />,
    },
    {
        key: 'colPciAddress',
        title: <Translate content="computeResource.vgpu.list.pciAddress" />,
    },
    {
        key: 'colStatus',
        title: <Translate content="computeResource.vgpu.list.status" />,
    },
    {
        key: 'colUuid',
        title: <Translate content="computeResource.vgpu.list.uuid" />,
    },
    {
        key: 'colIsPartitioned',
        title: <Translate content="computeResource.vgpu.list.partitioned" />,
    },
    {
        key: 'colProfile',
        title: <Translate content="computeResource.vgpu.list.profile" />,
    },
    {
        key: 'colVGpuUsage',
        title: <Translate content="computeResource.vgpu.list.vGpuUsage" />,
    },
    {
        key: 'colActions',
        title: '',
        width: '1%',
    },
];

const STATUS_TRANSLATION_KEYS: Record<GpuStatus, string> = {
    active: 'computeResource.vgpu.status.active',
    not_found: 'computeResource.vgpu.status.notFound',
    conflict: 'computeResource.vgpu.status.conflict',
};

const STATUS_INTENT_MAP: Record<GpuStatus, 'success' | 'warning' | 'danger'> = {
    active: 'success',
    not_found: 'danger',
    conflict: 'warning',
};

const VGPUTab: FC<VGPUTabProps> = function ({
    computeResource,
    gpus,
    isLoading,
    isPartitioning,
    isRefreshing,
    computeResourceActions: {
        getGpus,
        partitionGpu,
        refreshGpus,
    },
}) {
    const [selectedGpu, setSelectedGpu] = React.useState<IGpuResponse | null>(null);
    const [selectedProfile, setSelectedProfile] = React.useState<IProfileSelectOption | null>(null);

    const openDialog = React.useCallback((gpu: IGpuResponse) => {
        setSelectedGpu(gpu);
        setSelectedProfile(null);
    }, []);

    const closeDialog = React.useCallback(() => {
        setSelectedGpu(null);
        setSelectedProfile(null);
    }, []);

    const handleApply = React.useCallback(async () => {
        if (!selectedGpu || !selectedProfile) {
            return;
        }

        try {
            await partitionGpu(selectedGpu.id, selectedProfile.value);
            closeDialog();
        } catch {
            // Form errors are handled by the global error handler.
        }
    }, [selectedGpu, selectedProfile, partitionGpu, closeDialog]);

    React.useEffect(() => {
        computeResource.id && computeResource.gpu_driver_version && getGpus(computeResource.id);
    }, [getGpus, computeResource.id, computeResource.gpu_driver_version]);

    const data = React.useMemo(() => gpus.map((item, ind) => {
        const vsCount = item.placement_addresses.filter(p => p.server).length;
        const hasVms = vsCount > 0;
        const isNotActive = item.status !== 'active';
        const isDisabled = hasVms || isNotActive;

        let tooltipKey = 'computeResource.vgpu.tooltip.partition';
        if (hasVms) {
            tooltipKey = 'computeResource.vgpu.tooltip.disabledHasVms';
        } else if (isNotActive) {
            tooltipKey = 'computeResource.vgpu.tooltip.disabledNotActive';
        }

        return {
            id: item.id,
            colInd: ind + 1,
            colName: item.name,
            colPciAddress: item.pci_address,
            colUuid: item.uuid,
            colIsPartitioned: item.is_partitioned
                ? <Translate content="computeResource.vgpu.partitionedYes" />
                : <Translate content="computeResource.vgpu.partitionedNo" />,
            colProfile: item.current_profile,
            colVGpuUsage: item.is_partitioned
                ? `${vsCount}/${item.placement_addresses.length}`
                : null,
            colStatus: (
                <Status intent={STATUS_INTENT_MAP[item.status]}>
                    <Translate content={STATUS_TRANSLATION_KEYS[item.status]} />
                </Status>
            ),
            colActions: isDisabled ? (
                <Tooltip title={<Translate content={tooltipKey} />}>
                    <div>
                        <Button
                            ghost={true}
                            icon={<Icon name={'pencil'} />}
                            disabled={true}
                        />
                    </div>
                </Tooltip>
            ) : (
                <Button
                    ghost={true}
                    icon={<Icon name={'pencil'} />}
                    onClick={() => openDialog(item)}
                />
            ),
            gpu: item,
        };
    }), [gpus, openDialog]);

    const profileOptions: IProfileSelectOption[] = React.useMemo(() => {
        if (!selectedGpu) {
            return [];
        }

        return selectedGpu.available_profiles.map(profile => ({
            value: profile.id,
            label: profile.name,
            profile,
        }));
    }, [selectedGpu]);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    {computeResource.gpu_driver_version && (
                        <Translate
                            content="computeResource.vgpu.driverVersion"
                            params={{ version: computeResource.gpu_driver_version }}
                        />
                    )}
                </div>

                <Button
                    ghost={true}
                    state={isRefreshing ? 'loading' : undefined}
                    onClick={() => computeResource.id && refreshGpus(computeResource.id)}
                >
                    <Translate content="computeResource.vgpu.refreshBtn" />
                </Button>
            </div>

            <Loader isLoading={isLoading}>
                <List
                    columns={COLUMNS}
                    data={data}
                    renderRowBody={(row) => {
                        const { gpu } = row;
                        const hasConflicts = gpu.status === 'conflict'
                            && gpu.conflict_details !== null
                            && gpu.conflict_details.length > 0;
                        const hasPlacements = gpu.placement_addresses.length > 0;

                        return (
                            <>
                                {hasConflicts && (
                                    <div style={{ marginBottom: hasPlacements ? 15 : 0 }}>
                                        <Alert intent={INTENT_TYPE.WARNING}>
                                            {gpu.conflict_details!.map((detail, i) => (
                                                <div key={i}>
                                                    <Translate
                                                        content={`computeResource.vgpu.conflict.${detail.key}`}
                                                        params={detail.params}
                                                    />
                                                </div>
                                            ))}
                                        </Alert>
                                    </div>
                                )}
                                {hasPlacements
                                    ? <VGPUPlacements gpu={gpu} />
                                    : !hasConflicts && (
                                        <Translate content="computeResource.vgpu.noPlacements" />
                                    )
                                }
                            </>
                        );
                    }}
                    rowKey={'id'}
                    emptyView={
                        <Alert><Translate content="computeResource.vgpu.emptyView" /></Alert>
                    }
                />
            </Loader>

            <Dialog
                isOpen={selectedGpu !== null}
                closeHandler={closeDialog}
                size={'sm'}
                heading={<Translate content="computeResource.vgpu.dialog.title" />}
            >
                <FormField
                    label={<Translate content="computeResource.vgpu.dialog.selectProfile" />}
                    vertical
                >
                    {({ getId }) => (
                        <SelectInput<IProfileSelectOption>
                            inputId={getId()}
                            menuPosition="fixed"
                            options={profileOptions}
                            isClearable={false}
                            value={selectedProfile}
                            onChange={(option) => setSelectedProfile(option as IProfileSelectOption)}
                            placeholder={<Translate content="computeResource.vgpu.dialog.selectPlaceholder" />}
                        />
                    )}
                </FormField>

                {selectedProfile && (
                    <table style={{ width: '100%', marginBottom: 15 }}>
                        <tbody>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.maxInstances" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.max_instances}</td>
                            </tr>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.frameRateLimit" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.frame_rate_limit_fps} fps</td>
                            </tr>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.frameBufferMemory" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.frame_buffer_memory_mb} MB</td>
                            </tr>
                            <tr>
                                <th style={{ padding: 5, textAlign: 'left' }}><Translate content="computeResource.vgpu.dialog.gridLicense" /></th>
                                <td style={{ padding: 5 }}>{selectedProfile.profile.grid_license}</td>
                            </tr>
                        </tbody>
                    </table>
                )}

                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    size={SIZE.LG}
                    fill={true}
                    disabled={!selectedProfile}
                    state={isPartitioning ? 'loading' : undefined}
                    onClick={handleApply}
                >
                    <Translate content="computeResource.vgpu.dialog.applyBtn" />
                </Button>
            </Dialog>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    computeResource: state.computeResource.item,
    gpus: state.computeResource.gpus,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_GPUS),
    isPartitioning: state.app.loadingFlags.has(LOADING_FLAGS.PARTITION_GPU),
    isRefreshing: state.app.loadingFlags.has(LOADING_FLAGS.REFRESH_COMPUTE_RESOURCE_GPUS),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(VGPUTab);
